import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  User, 
  Settings, 
  LogOut, 
  Building2,
  CreditCard,
  HelpCircle,
  Mail,
  ShieldCheck
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getImageUrl } from "../lib/utils";

export default function UserMenu({ variant = "default" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = user?.email === "admin@tireo.app" || user?.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Get initials from company name or contact name
  const getInitials = () => {
    const name = user?.company_name || user?.contact_name || user?.name || "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get display name
  const getDisplayName = () => {
    return user?.company_name || user?.contact_name || user?.name || "Utilisateur";
  };

  const textColorClass = variant === "light" ? "text-white" : "text-foreground";
  const mutedColorClass = variant === "light" ? "text-white/70" : "text-muted-foreground";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className={`flex items-center gap-2 sm:gap-3 h-auto py-2 px-2 sm:px-3 ${variant === "light" ? "hover:bg-white/20" : "hover:bg-muted"}`}
          data-testid="user-menu-btn"
        >
          <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
            <AvatarImage src={getImageUrl(user?.logo_url)} alt={getDisplayName()} />
            <AvatarFallback className="bg-primary text-white text-xs sm:text-sm font-medium">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:flex flex-col items-start">
            <span className={`text-sm font-medium ${textColorClass} truncate max-w-[150px]`}>
              {getDisplayName()}
            </span>
            <span className={`text-xs ${mutedColorClass} truncate max-w-[150px]`}>
              {user?.email}
            </span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Paramètres
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/company" className="cursor-pointer">
            <Building2 className="mr-2 h-4 w-4" />
            {user?.client_type === "association" ? "Mon association" : "Mon entreprise"}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/billing" className="cursor-pointer">
            <CreditCard className="mr-2 h-4 w-4" />
            Facturation
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/email" className="cursor-pointer">
            <Mail className="mr-2 h-4 w-4" />
            Paramètres Email
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isAdmin && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/admin" className="cursor-pointer text-red-600">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Administration
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem asChild>
          <a href="mailto:support@tireo.app" className="cursor-pointer">
            <HelpCircle className="mr-2 h-4 w-4" />
            Aide
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
